<script setup>
import { computed, ref, watch } from 'vue';

import { DEFAULT_QUERY_PARAMS } from '@pfsc/core/models/query-params';
import { useTriggerStore } from '@pfsc/core/stores/trigger.store';
import { useRoute, useRouter } from 'vue-router';
import { useTabsManagementStore } from '@pfsc/core/stores/tabs-management.store';
import { storeToRefs } from 'pinia';
import { ordersService } from '@pfsc/core/ui-services/orders.service';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { globalTranslate } from '../locale/app.i18n';
import AppPagination from './app-pagination.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  shouldShowInfo: { type: Boolean, default: false },
  selectedRegistrations: { type: Array, default: () => [] },
  headers: { type: Array, default: () => [] },
  items: { type: Array, default: () => [] },
  page: { type: Number, default: DEFAULT_QUERY_PARAMS.page },
  perPage: { type: Number, default: DEFAULT_QUERY_PARAMS.perPage },
  sort: { type: Object, default: () => ({}) },
  totalResult: { type: Number, default: 0 },
  totalPages: { type: Number, default: 0 },
  isItemsLoading: { type: Boolean, default: false },
  fetchItems: { type: Function, default: () => {} },
  isExpandable: { type: Boolean, default: false },
  itemKey: { type: String, default: 'id' },
  canRowToggle: { type: Boolean, default: false },
  isExpandedRowShown: { type: Boolean, default: false },
  isCheckboxShown: { type: Boolean, default: true },
});

const tabsManagementStore = useTabsManagementStore();
const { currentTab } = storeToRefs(tabsManagementStore);
const route = useRoute();
const router = useRouter();
const trigger = useTriggerStore();
const authStore = useAuthStore();

const isPfsAdmin = computed(() => authStore.currentUser?.isPfsAdmin);
const isGroupAdmin = computed(() => authStore.currentUser?.isGroupAdmin);
const isSortEmpty = computed(() => Object.keys(props.sort).length === 0);

// When show-select is true, the table will have this ID for the select checkbox row.
// However, if we use a custom #header to render the table, the select all rows checkbox will also be hidden
// Therefore, we need to manually handle selecting all rows again.
const selectRowColumnId = 'data-table-select';

const emits = defineEmits(['on-page-change', 'update:model-value', 'row-selected']);

const selectedRow = ref(null);
const expanded = ref([]);
const selectedId = ref(null);

const isSelectAllRow = computed(() => props.items.length > 0
  && props.items.every(item => props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey])));

const isSelectIndeterminate = computed(() => {
  const selectedInItems = props.items.filter(item => props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey]));

  return selectedInItems.length > 0 && selectedInItems.length < props.items.length;
});

const isActiveRow = item => computed(() => selectedRow?.value?.id === item.raw.id);

/**
 * Handle page changes.
 * @param {object} options New page.
 */
function onPageChange(options) {
  emits('on-page-change', options);
  // The function update:options do not emit when items-per-page update => we need to fetch items manually here.
  if (options.perPage !== props.perPage || options.page !== props.page) {
    props.fetchItems(props.sort);
  }
}

/**
 * Check if an item is selected.
 * @param {object} item Item to check.
 * @returns {boolean}
 */
const isItemSelected = item => props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey]);

/**
 * Toggle selection for a single item.
 * @param {object} item Item to toggle.
 */
const toggleItemSelection = item => {
  const isSelected = isItemSelected(item);
  let newSelection;
  if (isSelected) {
    newSelection = props.modelValue.filter(s => s[props.itemKey] !== item[props.itemKey]);
  } else {
    newSelection = [...props.modelValue, item];
  }
  emits('update:model-value', newSelection);
};

/**
 * Handle select all row.
 * @param {boolean} isChecked Checkbox event.
 */
function onSelectAllRow(isChecked) {
  const currentItemIds = new Set(props.items.map(i => i[props.itemKey]));
  let newSelection;
  if (isChecked) {
    // Add all current page items to selection (avoid duplicates)
    const newItems = props.items.filter(item => !isItemSelected(item));
    newSelection = [...props.modelValue, ...newItems];
  } else {
    // Remove all current page items from selection
    newSelection = props.modelValue.filter(s => !currentItemIds.has(s[props.itemKey]));
  }
  emits('update:model-value', newSelection);
}

/**
 * Handle sort change.
 * @param {object} sortValue Sort value.
 */
function onSortChange(sortValue) {
  props.fetchItems({ sortBy: sortValue });
}

/**
 * Update selectedRow and selectedId based on table row selected
 * @param {object} row Select row
 */
function selectRow(row) {
  selectedRow.value = (row == null || selectedRow.value?.id === row.id) ? null : row;
  selectedId.value = selectedRow?.value ? selectedRow?.value?.id : null;
  const newQuery = {
    tab: currentTab?.value ?? null,
  };

  if (selectedId.value) {
    newQuery.confirmationId = selectedId.value;
  }
  router.push({ query: newQuery });
  emits('row-selected', selectedRow.value);
}

/**
 * Toggle selection state for a row when allowed.
 * @param {object} row Row wrapper from the table slot.
 * @returns {void}
 */
function toggleRow(row) {
  if (props.canRowToggle) {
    selectRow(row.raw);
  }
}

/**
 * Clear selected row from trigger
 */
watch(
  () => trigger.clearSelectedRowChange,
  () => {
    selectedRow.value = null;
    router?.push({ query: { tab: currentTab?.value ?? null } });
  },
);

/**
 * Update selectedId based on confirmationId query param
 */
watch(() => route?.query?.confirmationId, async newValue => {
  selectedId.value = newValue ? Number(newValue) : null;
}, { immediate: true });

/**
 * Set selectedRow once data is done loading
 */
watch(
  () => props.isItemsLoading,
  isLoading => {
    if (!isLoading) {
      selectedRow.value = props.items.find(itm => itm.id === selectedId.value);
      selectedId.value = null;

      if (selectedRow.value) {
        emits('row-selected', selectedRow.value);
      }
    }
  },
);

defineExpose({
  selectedRow,
});

</script>
<template>
  <v-data-table-server
    v-model:expanded="expanded"
    :model-value="modelValue"
    :page="page"
    :item-per-page="perPage"
    :items="items"
    :items-length="totalResult"
    :headers="headers"
    :loading="isItemsLoading"
    :sticky="true"
    :sort-by="isSortEmpty ? undefined :sort"
    :show-expand="isExpandable"
    :item-key="itemKey"
    :item-value="itemKey"
    hide-default-footer
    :no-data-text="globalTranslate('table.noDataText')"
    first-icon="mdi:mdi-page-first"
    last-icon="mdi:mdi-page-last"
    next-icon="mdi:mdi-chevron-right"
    return-object
    prev-icon="mdi:mdi-chevron-left"
    sort-asc-icon="fas fa-sort-up"
    sort-desc-icon="fas fa-sort-down"
    :class="$style.table"
    :select-all="isCheckboxShown"
    fixed-header
    @update:sort-by="onSortChange"
  >
    <template
      #headers="{ columns, isSorted, getSortIcon, toggleSort, sortBy }"
    >
      <tr>
        <td
          v-if="isCheckboxShown"
          :class="[$style.checkboxHeader, $style.header]"
        >
          <div :class="$style.headerWrapper">
            <v-checkbox-btn
              v-model="isSelectAllRow"
              :indeterminate="isSelectIndeterminate"
              :disabled="isItemsLoading"
              @update:model-value="onSelectAllRow"
            />
          </div>
        </td>
        <template v-for="column in columns" :key="column.key">
          <td
            v-if="column.key !== selectRowColumnId"
            :class="$style.header"
            :style="{width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth}"
          >
            <div
              :class="[$style.headerWrapper, column.sortable && !isItemsLoading && $style.pointerCursor]"
            >
              <span
                :style="{ textAlign: column.align ?? 'left' }"
                :class="$style.headerTitle"
                @click="
                  () => {
                    if (column.sortable && !isItemsLoading) {
                      toggleSort(column);
                    }
                  }
                "
              >{{ column.title }}
                <template v-if="isSorted(column)">
                  <v-icon :class="$style['sort-icon']" :icon="getSortIcon(column)" />
                </template>
                <template v-if="column.sortable && sortBy.length === 0">
                  <v-icon :class="$style['sort-icon']" icon="fas fa-sort" />
                </template>
              </span>
            </div>
          </td>
        </template>
      </tr>
    </template>

    <template #item="{ internalItem: item, columns }">
      <tr
        :class="[
          $style.courseRow,
          isActiveRow(item).value && $style.activeRow,
          canRowToggle && $style.pointerCursor
        ]"
        @click="toggleRow(item)"
      >
        <td v-if="isCheckboxShown">
          <v-checkbox
            :class="$style.checkbox"
            :model-value="isItemSelected(item.raw)"
            :disabled="isItemsLoading"
            @update:model-value="toggleItemSelection(item.raw)"
            @click.stop
          />
        </td>
        <td
          v-for="column in headers"
          :key="column.key"
          :class="{ ['text-' + column.align]: column.align }"
        >
          <slot
            :name="column.key"
            :item="item"
            :column="column"
          >
            {{ item.columns[column.key] }}
          </slot>
        </td>

        <!-- Custom expandable icon. -->
        <td v-if="isExpandable" class="text-start">
          <slot
            name="expandIcon"
            :item="item"
            :is-expanded="isActiveRow(item)"
          >
            <v-btn
              :class="[
                $style.expandButton,
                isActiveRow(item) && $style.expandButtonActive,
              ]"
            >
              {{ globalTranslate('table.viewInfo') }}
            </v-btn>
            <div
              v-if="isPfsAdmin == true || isGroupAdmin == true"
              :class="$style.payNowButton"
            >
              <v-btn
                v-if="item.raw.balance > 0 || item.raw?.registration?.balance > 0"
                :class="[$style.expandButton]"
                @click="ordersService.payNow(item)"
              >
                {{ globalTranslate('newOrders.payNow') }}
              </v-btn>
            </div>
          </slot>
        </td>
      </tr>
      <!-- Expanded Row -->
      <slot
        v-if="isActiveRow(item) && props.isExpandedRowShown"
        name="expanded"
        :item="item"
        :columns="columns"
        :is-expanded="isActiveRow(item)"
      />
    </template>

    <!-- Footer -->
    <template #bottom>
      <app-pagination
        :page="page"
        :per-page="perPage"
        :total="totalResult"
        :total-pages="totalPages"
        :disabled="isItemsLoading"
        @on-page-change="onPageChange"
      />
    </template>
  </v-data-table-server>
</template>

<style module>

.table {
  font-size: var(--font-size-md);
}

.checkboxShown {
  padding: 0 8px !important;
}

.header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-weight: 600;
  width: fit-content;
  background: white;
}

.headerTitle {
  width: 100%;
  align-items: center;
}

.headerWrapper {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: var(--header-justify-content, flex-start);
}

tr {
  cursor: default;
}
.pointerCursor {
  cursor: pointer;
}

.expandButton {
  min-width: 45px;
  color: var(--secondary-color);
  margin-top: 5px;
}

.expandButtonActive {
  color: var(--primary-dark-color);
}

.checkbox {
  display: flex;
  i {
    font-size: 19.5px;
  }
}

.activeRow {
  background-color: var(--table-cell-background-color-darker);
}

.payNowButton {
  margin: 5px auto;
  border-color: var(--pfs-red);
  color: var(--pfs-red);
}

.sort-icon {
  font-size: 16px;
  height: 16px;
  width: 16px;
  margin-bottom: 4px;
}
</style>
